<template lang="pug">
  .review-session
    .container

      .review-header
        a.review-back.is-flex.has-flex-center-items(@click="goBack")
          b-icon(icon="arrow-back" size="is-small")
          span.ml-1 Back
        h1.title.is-4.review-title Review
        .review-streak(v-if="stats.streak_days > 0")
          span 🔥
          span.ml-1 {{ stats.streak_days }} day{{ stats.streak_days > 1 ? 's' : '' }}

      //- ── loading ──────────────────────────────────────────────────────────
      .review-panel(v-if="phase === 'loading'")
        .has-loader.is-primary.is-outlined.is-loading.has-margin-auto.primary-loader

      //- ── error ────────────────────────────────────────────────────────────
      .review-panel(v-else-if="phase === 'error'")
        p.is-size-5.mb-4 We could not load your cards.
        button.button.is-primary(@click="start") Try again

      //- ── never added a card ───────────────────────────────────────────────
      .review-panel(v-else-if="phase === 'empty'")
        p.is-size-4.mb-2 No cards yet
        p.has-text-grey.mb-5
          | Open a lesson and tap
          strong  Add to my reviews
          |  to start building your deck.
        router-link.button.is-primary(:to="{ name: 'VisitedLessons' }") Go to my lessons

      //- ── nothing due right now ────────────────────────────────────────────
      .review-panel(v-else-if="phase === 'caught-up'")
        p.is-size-3.mb-2 All caught up
        p.has-text-grey.mb-5(v-if="stats.total_cards")
          | {{ stats.total_cards }} card{{ stats.total_cards > 1 ? 's' : '' }} in your deck
        .buttons.is-centered
          button.button.is-primary.is-outlined(@click="startAhead") Review ahead
          router-link.button.is-text(:to="{ name: 'VisitedLessons' }") Back to my lessons

      //- ── session complete ─────────────────────────────────────────────────
      .review-panel(v-else-if="phase === 'complete'")
        p.is-size-3.mb-4 Session complete
        .review-summary
          .review-summary-item
            .review-summary-number {{ reviewedCount }}
            .review-summary-label reviewed
          .review-summary-item(v-if="stats.streak_days > 0")
            .review-summary-number 🔥 {{ stats.streak_days }}
            .review-summary-label day streak
        .review-breakdown
          span.tag.is-danger.is-light Again {{ counts[2] }}
          span.tag.is-warning.is-light.ml-2 Hesitated {{ counts[4] }}
          span.tag.is-success.is-light.ml-2 Easy {{ counts[5] }}
        p.has-text-grey.mt-4(v-if="pendingCount > 0")
          | {{ pendingCount }} answer{{ pendingCount > 1 ? 's' : '' }} still syncing — they will be sent automatically.
        .buttons.is-centered.mt-5
          button.button.is-primary.is-outlined(v-if="stats.due_now > 0" @click="start")
            | Keep going ({{ stats.due_now }} due)
          router-link.button.is-text(:to="{ name: 'VisitedLessons' }") Back to my lessons

      //- ── the session itself ───────────────────────────────────────────────
      template(v-else-if="phase === 'session' && current")
        .review-progress
          progress.progress.is-primary.is-small(:value="reviewedCount" :max="deck.length")
          .review-progress-label {{ reviewedCount }} / {{ deck.length }}

        .review-card(
          @click="reveal"
          @touchstart="onTouchStart"
          @touchend="onTouchEnd"
          :class="{ 'is-revealed': revealed }"
        )
          .review-card-origin(v-if="lessonTitle") {{ lessonTitle }}

          img.review-picture(v-if="pictureUrl" :src="pictureUrl" alt="")

          .review-front
            p.review-primary {{ frontText }}
            p.review-ipa(v-if="showIpa") {{ ipa }}
            button.button.is-small.is-rounded.review-audio(
              v-if="hasSound"
              @click.stop="playSound"
              title="Replay (R)"
            )
              b-icon(icon="volume-up" size="is-small")

          .review-divider(v-if="!revealed") tap or press space to reveal

          .review-back-face(v-if="revealed")
            p.review-secondary {{ backText }}
            p.review-literal(v-if="backLiteral") {{ backLiteral }}
            p.review-comment(v-if="sentence.comment") {{ sentence.comment }}

        .review-grades
          button.button.review-grade.is-danger.is-light(:disabled="!revealed" @click="grade(2)")
            span.review-grade-label Again
            span.review-grade-hint {{ preview(2) }}
            span.review-grade-key 1
          button.button.review-grade.is-warning.is-light(:disabled="!revealed" @click="grade(4)")
            span.review-grade-label Hesitated
            span.review-grade-hint {{ preview(4) }}
            span.review-grade-key 2
          button.button.review-grade.is-success.is-light(:disabled="!revealed" @click="grade(5)")
            span.review-grade-label Easy
            span.review-grade-hint {{ preview(5) }}
            span.review-grade-key 3

        .review-footer
          a.review-mute(@click="toggleMute")
            b-icon(:icon="muted ? 'volume-off' : 'volume-up'" size="is-small")
            span.ml-1 {{ muted ? 'Sound off' : 'Sound on' }}
</template>


<script>
import {mapGetters} from "vuex";
import {nextInterval, humanizeInterval} from "@/utils/supermemo";

const AGAIN = 2;
const HESITATED = 4;
const EASY = 5;

const SWIPE_THRESHOLD = 60;

export default {
  name: "ReviewSession",

  data() {
    return {
      phase: "loading",       // loading | error | empty | caught-up | session | complete
      deck: [],
      position: 0,
      revealed: false,
      reviewedCount: 0,
      counts: {[AGAIN]: 0, [HESITATED]: 0, [EASY]: 0},
      muted: false,
      touchStart: null,
      sounds: {},
    };
  },

  computed: {
    ...mapGetters(["flashcards", "flashcardStats", "flashcardQueue", "apiUrl"]),

    stats() {
      return this.flashcardStats || {};
    },

    pendingCount() {
      return (this.flashcardQueue || []).length;
    },

    current() {
      return this.deck[this.position] || null;
    },

    sentence() {
      return (this.current && this.current.sentence) || {};
    },

    lessonTitle() {
      return (this.sentence.lesson || {}).title;
    },

    // `unknown_to_front` puts the language being learned on the front of the card;
    // `unknown_to_back` asks the student to produce it instead.
    frontIsTarget() {
      return !this.current || this.current.mode !== "unknown_to_back";
    },

    frontText() {
      return this.frontIsTarget ? this.sentence.content : this.sentence.translation;
    },

    backText() {
      return this.frontIsTarget ? this.sentence.translation : this.sentence.content;
    },

    backLiteral() {
      // The literal gloss explains the target language, so it only makes sense
      // once that side is showing.
      return this.frontIsTarget ? this.sentence.literal_translation : null;
    },

    ipa() {
      return this.sentence.ipa;
    },

    // Phonetics belong to the target language, so they stay hidden until that
    // side of the card is visible — otherwise they give the answer away.
    showIpa() {
      return !!this.sentence.ipa && (this.frontIsTarget || this.revealed);
    },

    pictureUrl() {
      const picture = this.sentence.picture;
      if (!picture) {
        return null;
      }
      const path = picture.thumb_url || picture.url;
      return path ? `${this.apiUrl}/${path}` : null;
    },

    audioPath() {
      const audio = (this.sentence.audio || [])[0];
      return audio && audio.path ? `${this.apiUrl}/${audio.path}` : null;
    },

    hasSound() {
      return !!this.audioPath || !!this.speechText;
    },

    speechText() {
      return this.sentence.content;
    },

    speechLanguage() {
      return (this.sentence.lesson || {}).language_to;
    },
  },

  created() {
    this.start();
    // Anything left over from a session that lost connectivity goes out now.
    this.$store.dispatch("flushFlashcardReviews");
  },

  mounted() {
    window.addEventListener("keydown", this.onKey);
  },

  beforeDestroy() {
    window.removeEventListener("keydown", this.onKey);
    this.stopSound();
  },

  methods: {
    start() {
      this.phase = "loading";
      this.resetSession();

      this.$store.dispatch("fetchDueFlashcards").then(
        () => {
          this.$store.dispatch("fetchFlashcardStats").catch(() => {});
          this.buildDeck(this.flashcards);
        },
        () => {
          this.phase = "error";
        }
      );
    },

    // "Review ahead" — the student is caught up but wants to keep going, so we
    // pull the next cards regardless of due date.
    startAhead() {
      this.phase = "loading";
      this.resetSession();

      this.$store.dispatch("fetchFlashcardsAhead").then(
        () => this.buildDeck(this.flashcards),
        () => { this.phase = "error"; }
      );
    },

    resetSession() {
      this.deck = [];
      this.position = 0;
      this.revealed = false;
      this.reviewedCount = 0;
      this.counts = {[AGAIN]: 0, [HESITATED]: 0, [EASY]: 0};
      this.sounds = {};
    },

    buildDeck(flashcards) {
      // A card whose sentence was emptied has nothing to show; drop it rather
      // than rendering a blank card the student cannot answer.
      const usable = (flashcards || []).filter(
        f => f.sentence && (f.sentence.content || f.sentence.translation)
      );

      if (!usable.length) {
        this.phase = this.stats.total_cards > 0 ? "caught-up" : "empty";
        return;
      }

      this.deck = usable;
      this.phase = "session";
      this.preloadSounds();
    },

    reveal() {
      if (this.revealed) {
        return;
      }
      this.revealed = true;
      this.playSound();
    },

    grade(value) {
      // Grading blind would poison the schedule, so the buttons stay inert until
      // the answer has actually been seen.
      if (!this.revealed || !this.current) {
        return;
      }

      const card = this.current;
      this.$set(this.counts, value, (this.counts[value] || 0) + 1);
      this.reviewedCount++;

      this.$store.dispatch("gradeFlashcard", {flashcard: card, grade: value});

      // A failed card is scheduled for tomorrow server-side, but it should come
      // back before the student leaves — so it is re-queued for this session only.
      if (value < 3) {
        this.deck.push(card);
      }

      this.stopSound();
      this.position++;
      this.revealed = false;

      if (!this.current) {
        this.phase = "complete";
        this.$store.dispatch("fetchFlashcardStats").catch(() => {});
      } else {
        this.preloadSounds();
      }
    },

    preview(value) {
      return humanizeInterval(nextInterval(this.current, value));
    },

    // ── sound ──────────────────────────────────────────────────────────────
    preloadSounds() {
      for (let i = this.position; i < Math.min(this.position + 3, this.deck.length); i++) {
        const card = this.deck[i];
        const audio = ((card.sentence && card.sentence.audio) || [])[0];
        if (audio && audio.path && !this.sounds[card.id]) {
          const sound = new Audio(`${this.apiUrl}/${audio.path}`);
          sound.preload = "auto";
          this.sounds[card.id] = sound;
        }
      }
    },

    playSound() {
      if (this.muted || !this.current) {
        return;
      }

      const sound = this.sounds[this.current.id];
      if (sound) {
        sound.currentTime = 0;
        // Autoplay can be refused before the student has interacted with the
        // page; that is not worth an error, they can press the speaker.
        const played = sound.play();
        if (played && played.catch) {
          played.catch(() => {});
        }
        return;
      }

      if (this.audioPath) {
        return;
      }

      this.speak();
    },

    // No teacher recording for this sentence — fall back to the browser voice,
    // using the same language/voice map the lesson page uses.
    speak() {
      if (!window.speechSynthesis || !this.speechText || !this.speechLanguage) {
        return;
      }

      const utterance = new SpeechSynthesisUtterance(this.speechText);
      utterance.lang = this.speechLanguage;
      utterance.volume = 0.75;

      const voiceName = (this.$store.state.voices || {})[this.speechLanguage];
      if (voiceName) {
        const voice = window.speechSynthesis.getVoices().find(v => v.name === voiceName);
        if (voice) {
          utterance.voice = voice;
        }
      }

      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utterance);
    },

    stopSound() {
      const sound = this.current && this.sounds[this.current.id];
      if (sound) {
        sound.pause();
      }
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    },

    toggleMute() {
      this.muted = !this.muted;
      if (this.muted) {
        this.stopSound();
      }
    },

    // ── input ──────────────────────────────────────────────────────────────
    onKey(event) {
      if (this.phase !== "session") {
        return;
      }
      // Never hijack keys while the student is typing somewhere.
      const tag = (event.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || event.target.isContentEditable) {
        return;
      }

      switch (event.key) {
        case " ":
        case "Enter":
          event.preventDefault();
          this.reveal();
          break;
        case "1":
          this.grade(AGAIN);
          break;
        case "2":
          this.grade(HESITATED);
          break;
        case "3":
          this.grade(EASY);
          break;
        case "r":
        case "R":
          this.playSound();
          break;
        default:
          break;
      }
    },

    onTouchStart(event) {
      const touch = event.changedTouches[0];
      this.touchStart = {x: touch.clientX, y: touch.clientY};
    },

    // Same gestures as the mobile app: left = Again, right = Easy, down = Hesitated.
    onTouchEnd(event) {
      if (!this.touchStart) {
        return;
      }
      const touch = event.changedTouches[0];
      const dx = touch.clientX - this.touchStart.x;
      const dy = touch.clientY - this.touchStart.y;
      this.touchStart = null;

      if (Math.max(Math.abs(dx), Math.abs(dy)) < SWIPE_THRESHOLD) {
        return; // a tap — @click already handles it
      }
      if (!this.revealed) {
        this.reveal();
        return;
      }

      if (Math.abs(dx) > Math.abs(dy)) {
        this.grade(dx < 0 ? AGAIN : EASY);
      } else if (dy > 0) {
        this.grade(HESITATED);
      }
    },

    goBack() {
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.push({name: "VisitedLessons"});
      }
    },
  },
};
</script>


<style lang="scss">
@import "colors";

.review-session {
  padding: 1.5rem 0 3rem;

  .review-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.5rem;
  }

  .review-back {
    color: #7a7a7a;
    white-space: nowrap;
    cursor: pointer;
  }

  .review-title {
    margin-bottom: 0;
  }

  .review-streak {
    white-space: nowrap;
    font-weight: 600;
  }

  .review-panel {
    text-align: center;
    padding: 4rem 1rem;
  }

  .review-progress {
    display: flex;
    align-items: center;
    margin-bottom: 1.25rem;

    .progress {
      margin-bottom: 0;
    }

    .review-progress-label {
      margin-left: 0.75rem;
      color: #7a7a7a;
      white-space: nowrap;
      font-size: 0.85rem;
    }
  }

  .review-card {
    background: #f3f0fe;
    border: 2px solid #e8e8e8;
    border-radius: 6px;
    padding: 2rem 1.5rem;
    min-height: 260px;
    cursor: pointer;
    user-select: none;
    // Vertical swipes must not be stolen by the browser's pull-to-refresh.
    touch-action: pan-y;

    &.is-revealed {
      cursor: default;
    }
  }

  .review-card-origin {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #b5b5b5;
    margin-bottom: 1rem;
  }

  .review-picture {
    max-height: 140px;
    border-radius: 4px;
    margin-bottom: 1rem;
  }

  .review-primary {
    font-size: 1.9rem;
    font-weight: 800;
    line-height: 1.2;
  }

  .review-ipa {
    color: #7a7a7a;
    margin-top: 0.35rem;
  }

  .review-audio {
    margin-top: 0.75rem;
  }

  .review-divider {
    margin-top: 2rem;
    text-align: center;
    color: #b5b5b5;
    font-size: 0.8rem;
  }

  .review-back-face {
    margin-top: 1.5rem;
    padding-top: 1.25rem;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }

  .review-secondary {
    font-size: 1.4rem;
    font-weight: 700;
    color: $primary;
  }

  .review-literal {
    color: #7a7a7a;
    margin-top: 0.35rem;
  }

  .review-comment {
    color: #7a7a7a;
    font-size: 0.9rem;
    margin-top: 0.5rem;
  }

  .review-grades {
    display: flex;
    margin-top: 1.25rem;

    .review-grade {
      flex: 1;
      height: auto;
      flex-direction: column;
      padding: 0.75rem 0.5rem;
      margin-right: 0.75rem;

      &:last-child {
        margin-right: 0;
      }
    }

    .review-grade-label {
      font-weight: 700;
    }

    .review-grade-hint {
      font-size: 0.75rem;
      opacity: 0.7;
    }

    .review-grade-key {
      font-size: 0.7rem;
      opacity: 0.45;
      margin-top: 0.15rem;
    }
  }

  .review-summary {
    display: flex;
    justify-content: center;
    margin-bottom: 1.5rem;
  }

  .review-summary-item {
    margin: 0 1.5rem;
  }

  .review-summary-number {
    font-size: 2.4rem;
    font-weight: 800;
  }

  .review-summary-label {
    color: #7a7a7a;
    font-size: 0.85rem;
  }

  .review-mute {
    color: #7a7a7a;
    cursor: pointer;
  }

  .review-footer {
    margin-top: 1.25rem;
    text-align: center;
    color: #7a7a7a;
    font-size: 0.85rem;
  }
}

@media screen and (max-width: 768px) {
  .review-session .review-primary {
    font-size: 1.5rem;
  }
}
</style>
